'use client';

import React, { useEffect, useRef, useState } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import { Play, Pause, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { formatTime } from '@/lib/utils/general';

interface InterviewAudioPlayerProps {
  audioUrl: string;
  onTimeUpdate?: (currentTime: number) => void;
  onLoadedMetadata?: (duration: number) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onSeeking?: (time: number) => void;
  seekToTime?: number;
  className?: string;
}

export function InterviewAudioPlayer({
  audioUrl,
  onTimeUpdate,
  onLoadedMetadata,
  onPlay,
  onPause,
  onSeeking,
  seekToTime,
  className = ''
}: InterviewAudioPlayerProps) {
  const playerRef = useRef<AudioPlayer>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Handle external seek requests (from transcript clicks)
  useEffect(() => {
    if (seekToTime !== undefined && seekToTime >= 0 && playerRef.current) {
      const audioElement = playerRef.current.audio.current;
      if (audioElement) {
        console.log('Seeking to time from transcript:', seekToTime);
        audioElement.currentTime = seekToTime;
        // Don't auto-play after seeking - let user control playback
      }
    }
  }, [seekToTime]);

  const handleListen = (e: Event) => {
    const audio = e.target as HTMLAudioElement;
    setCurrentTime(audio.currentTime);
    if (onTimeUpdate) {
      onTimeUpdate(audio.currentTime);
    }
  };

  const handleLoadedMetadata = (e: Event) => {
    const audio = e.target as HTMLAudioElement;
    console.log('Audio metadata loaded:', {
      duration: audio.duration,
      seekable: audio.seekable.length > 0,
      buffered: audio.buffered.length > 0
    });
    setDuration(audio.duration);
    if (onLoadedMetadata) {
      onLoadedMetadata(audio.duration);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
    if (onPlay) onPlay();
  };

  const handlePause = () => {
    setIsPlaying(false);
    if (onPause) onPause();
  };

  const togglePlayPause = () => {
    if (playerRef.current?.audio.current) {
      if (isPlaying) {
        playerRef.current.audio.current.pause();
      } else {
        playerRef.current.audio.current.play();
      }
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!playerRef.current?.audio.current || duration === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percent = Math.max(0, Math.min(1, clickX / width));
    const newTime = percent * duration;

    console.log('Progress bar seek to:', newTime, 'of', duration);
    playerRef.current.audio.current.currentTime = newTime;
  };

  return (
    <>
      {/* Hidden react-h5-audio-player for functionality */}
      <div style={{ display: 'none' }}>
        <AudioPlayer
          ref={playerRef}
          src={audioUrl}
          onListen={handleListen}
          onLoadedMetaData={handleLoadedMetadata}
          onPlay={handlePlay}
          onPause={handlePause}
          autoPlayAfterSrcChange={false}
          preload="auto"
        />
      </div>

      {/* Custom UI matching original design */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={togglePlayPause}
              className="rounded-full"
            >
              {isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
            </Button>
            <div
              className="flex-1 cursor-pointer"
              onClick={handleProgressClick}
            >
              <Progress
                value={duration > 0 ? (currentTime / duration) * 100 : 0}
                className="h-2"
              />
            </div>
            <span className="text-sm text-gray-500 font-mono">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <Volume2 className="w-4 h-4 text-gray-500" />
          </div>
        </div>
      </div>
    </>
  );
}
